<template>
    <div class="forgot">
        <div class="forgot__title">{{ forgotTitle }}</div>
        <div class="forgot__container">
            <div class="forgot__item">
                <div class="forgot__text">{{ forgotVisit }}</div>
                <div 
                    class="forgot__text forgot__text_bold" 
                    :class="forgotTextClasses"
                >
                    {{ visitLink }}
                </div>
            </div>
            <div class="forgot__delimiter">
                <div class="forgot__line" />
                <div class="delimiter__text"> {{ or }} </div>
                <div class="forgot__line" />
            </div>
            <div class="forgot__item" :class="forgotItemClasses">
                <div class="forgot__text">{{ forgotText }}</div>
                <qrcode-vue v-if="qrUrl" class="qr-code-medium forgot__qr-code" :value="qrUrl" :level="'H'" render-as="svg"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { isRiverTV, isVMedia, isFizzTV } from "@ca.vmedia.tv/domains";
    import QrcodeVue from 'qrcode.vue';
    import { useResources } from "@/composables/useResources";
    import { useCleanUrl } from "@/composables/useCleanUrl";
    import { ref, type Ref, onBeforeMount, onMounted, nextTick, computed, onBeforeUnmount } from "vue";
    import { useRouter } from 'vue-router'
    import type { AxiosError } from 'axios';
    import { Logger } from '@/utils';
    
    const { $i18n, $navigator } = useNuxtApp();
    const router = useRouter();
    const qrUrl: Ref<string> = ref('');
    const visitLink: Ref<string> = ref('');
    const forgotTitle: string = $i18n.t(Strings.LOGIN_FORGOT_RESET_PASSWORD).toString();
    const forgotVisit: string = $i18n.t(Strings.LOGIN_FORGOT_VISIT).toString();
    const forgotText: string = $i18n.t(Strings.LOGIN_FORGOT_SCAN).toString();
    const or: string = $i18n.t(Strings.LOGIN_FORGOT_OR).toString();
    const forgotTextClasses = computed((): {[key: string]: boolean} => {
        return {
            'forgot__text_vmedia': isVMedia, 
            'forgot__text_rivertv': isRiverTV, 
            'forgot__text_fizztv': isFizzTV,
            'forgot__text_skeleton': !qrUrl.value
        }
    });
    const setVisitLink = (url: string) => {
        visitLink.value = useCleanUrl(url);
    };

    const setQrUrl = (value: string) => {
        qrUrl.value = value;
    };

    const loadData = () => {
        useResources('forgot_password').then((qrUrlTemplate: string) => {
            setQrUrl(qrUrlTemplate);
            setVisitLink(qrUrlTemplate);
        }).catch((error) => {
            Logger.captureError(error as AxiosError | Error);
        });
    };
    const forgotItemClasses = computed((): {[key: string]: boolean} => {
        return {
            'forgot__item_skeleton': !qrUrl.value
        }
    });

    const registerKeyListeners = () => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = () => {
        $navigator.off("keypress:back", keyBackListener);
    };
    const keyBackListener = () => {
        router.go(-1);
    };

    onMounted(() => {
       void nextTick((): void => {
            registerKeyListeners();
        });
    });

    onBeforeMount(() => {
        loadData();
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
    });
</script>


<style lang="scss">
    @import url("styles.scss");
</style>